// The dot (Phelipe 2026-10-17): "click where it goes, drag it, then Done".
// Optional, next to "Draw where". The dot follows the mouse and snaps to the
// nearest place something can really go: between two fields of a form, at
// the start or end of a section, an empty place on a built-in form (above
// Weather Delay, under Workers at Jobsite…), or between two columns of a
// table Studio can change. Done gives the spot back as plain data the server
// applies as it is (section_key / after_key / before_key / position), plus
// the words for the line under the message ("after Workers on site").

import { landmarks } from "./page_blocks"
import { fieldLandmarks } from "./custom_fields"
import { blockAt, blockWords, elementPath } from "./anchor_element"

const PURPLE = "#7e22ce"
const LIGHT = "#a855f7"

const visible = (n) => !!n && n.getClientRects().length > 0
const text = (n) => (n?.textContent || "").replace(/\s+/g, " ").trim()

function fieldKey(cell) {
  const input = cell.querySelector("[name^='form_data[']")
  const m = input && /^form_data\[([^\]]+)\]/.exec(input.name)
  return m ? m[1] : null
}

function fieldLabel(cell) {
  return text(cell.querySelector("label")).replace(/\*/g, "").trim()
}

// An empty place on a built-in form is hidden (`empty:hidden`): its line sits
// in the gap under the block before it, or above the block after it.
function gapRect(box) {
  let prev = box.previousElementSibling
  while (prev && !visible(prev)) prev = prev.previousElementSibling
  let next = box.nextElementSibling
  while (next && !visible(next)) next = next.nextElementSibling
  if (prev) { const q = prev.getBoundingClientRect(); return { x: q.left, y: q.bottom + 8, w: q.width } }
  if (next) { const q = next.getBoundingClientRect(); return { x: q.left, y: q.top - 8, w: q.width } }
  const p = (box.parentElement || box).getBoundingClientRect()
  return { x: p.left, y: p.top, w: p.width }
}

// Every place on the page right now, in viewport coordinates. A vertical slot
// (h > 0) sits between two cells; a horizontal one (w > 0) is an empty place.
// `kinds` narrows to the places a change can use: "form" (a form's fields),
// "table" (a table's columns), "page" (a note, a button or a card on the page).
export function collectSlots({ kinds } = {}) {
  const want = (k) => !kinds || kinds.includes(k)
  const slots = []
  const form = want("form") ? document.querySelector('[data-controller~="report-form"]') : null
  if (form && visible(form)) {
    form.querySelectorAll("[data-section-key]").forEach((sec) => {
      if (!visible(sec) || sec.hasAttribute("data-studio-ghost")) return
      const grid = sec.querySelector(".grid")
      if (!grid) return
      const box = sec.closest("[data-studio-anchor]")
      const anchor = sec.dataset.sectionAnchor || box?.dataset.studioAnchor || null
      const section = text(sec.querySelector("h3")) || box?.dataset.studioAnchorLabel || sec.dataset.sectionKey
      const cells = [...grid.children].filter((c) => !c.hasAttribute("data-studio-ghost") && visible(c))
      cells.forEach((cell, i) => {
        const r = cell.getBoundingClientRect()
        slots.push({ kind: "form", x: r.left, y: r.top, h: r.height, w: 0, section_key: sec.dataset.sectionKey, anchor, position: i,
          before_key: fieldKey(cell), after_key: i ? fieldKey(cells[i - 1]) : null, section, field: fieldLabel(cell), prev: i ? fieldLabel(cells[i - 1]) : null })
      })
      if (cells.length) {
        const last = cells[cells.length - 1]
        const r = last.getBoundingClientRect()
        slots.push({ kind: "form", x: r.right, y: r.top, h: r.height, w: 0, section_key: sec.dataset.sectionKey, anchor, position: cells.length,
          after_key: fieldKey(last), before_key: null, section, prev: fieldLabel(last), end: true })
      }
    })
    // The places on a built-in form that have nothing in them yet — only on a
    // built-in type (a form of the company's own has its sections instead).
    const type = form.querySelector("#daily_report_daily_report_type_id")
    let builtin = false
    try {
      const all = JSON.parse(form.dataset.reportFormFormTemplatesByTypeValue || "{}")
      const schema = type && type.value ? all[type.value] : null
      builtin = !!type?.value && (!schema || schema.keep_builtin === true)
    } catch (e) { builtin = false }
    if (builtin) form.querySelectorAll("[data-studio-anchor]").forEach((box) => {
      if (box.querySelector("[data-section-key]")) return
      const r = gapRect(box)
      slots.push({ kind: "form", x: r.x, y: r.y, h: 0, w: r.w, section_key: box.dataset.studioAnchor, anchor: box.dataset.studioAnchor, position: 0,
        section: box.dataset.studioAnchorLabel || box.dataset.studioAnchor, empty: true })
    })
  }
  // A table Studio can change: before the first column, after each one.
  if (want("table")) document.querySelectorAll("table[data-studio-table]").forEach((table) => {
    if (!visible(table)) return
    const ths = [...table.querySelectorAll("thead th[data-column-key]")].filter(visible)
    if (!ths.length) return
    const page = table.dataset.studioTable
    const h = Math.min(table.getBoundingClientRect().height, 240)
    const r0 = ths[0].getBoundingClientRect()
    slots.push({ kind: "table", x: r0.left, y: r0.top, h, w: 0, page, position: 0, first: true })
    ths.forEach((th) => {
      const r = th.getBoundingClientRect()
      slots.push({ kind: "table", x: r.right, y: r.top, h, w: 0, page, after_key: th.dataset.columnKey, column: th.getAttribute("title") || text(th) })
    })
  })
  // The page itself (a note, a button or a card): its top, its bottom, and
  // before/after each heading, button and table on it.
  // A record's own fields (Studio step 3, 2026-10-19): the top and the bottom of the form, before/after each field.
  if (want("record")) {
    const holder = document.querySelector('[data-controller~="custom-fields"]')
    const root = holder ? (holder.closest("form, [data-custom-fields-root]") || holder.parentElement) : null
    if (holder && root && visible(root)) {
      const fields = fieldLandmarks(root).filter((l) => l.type === "field" && visible(l.el))
      const rr = root.getBoundingClientRect()
      const first = fields[0] ? fields[0].el.getBoundingClientRect() : null
      const hr = holder.getBoundingClientRect()
      slots.push({ kind: "record", x: rr.left + 16, y: first ? first.top - 8 : rr.top + 4, w: Math.max(rr.width - 32, 40), h: 0, spot: "form_top" })
      slots.push({ kind: "record", x: rr.left + 16, y: hr.height ? hr.bottom + 4 : hr.top, w: Math.max(rr.width - 32, 40), h: 0, spot: "form_bottom" })
      fields.forEach((l) => {
        const r = l.el.getBoundingClientRect()
        const lm = { type: "field", text: l.label || l.text, index: l.index }
        slots.push({ kind: "record", x: r.left, y: r.top - 6, w: Math.max(r.width, 40), h: 0, spot: "before", landmark: lm, field: lm.text })
        slots.push({ kind: "record", x: r.left, y: r.bottom + 6, w: Math.max(r.width, 40), h: 0, spot: "after", landmark: lm, field: lm.text })
      })
    }
  }
  if (want("page")) {
    const main = document.querySelector("main") || document.body
    const top = main.querySelector('[data-page-blocks-target="top"]')
    const bottom = main.querySelector('[data-page-blocks-target="bottom"]')
    const mr = main.getBoundingClientRect()
    const tr = top ? top.getBoundingClientRect() : null
    const br = bottom ? bottom.getBoundingClientRect() : null
    slots.push({ kind: "page", x: mr.left + 16, y: (tr && tr.height ? tr.bottom : mr.top + 4), w: Math.max(mr.width - 32, 40), h: 0, spot: "page_top" })
    slots.push({ kind: "page", x: mr.left + 16, y: (br && br.height ? br.top : mr.bottom - 4), w: Math.max(mr.width - 32, 40), h: 0, spot: "page_bottom" })
    landmarks(main).forEach((l) => {
      const r = l.el.getBoundingClientRect()
      const lm = { type: l.type, text: l.label, index: l.index }
      if (l.type === "button") {
        slots.push({ kind: "page", x: r.left - 6, y: r.top, h: r.height, w: 0, spot: "before", landmark: lm })
        slots.push({ kind: "page", x: r.right + 6, y: r.top, h: r.height, w: 0, spot: "after", landmark: lm })
      } else {
        slots.push({ kind: "page", x: r.left, y: r.top - 6, w: Math.max(r.width, 40), h: 0, spot: "before", landmark: lm })
        slots.push({ kind: "page", x: r.left, y: r.bottom + 6, w: Math.max(r.width, 40), h: 0, spot: "after", landmark: lm })
      }
    })
  }
  return slots
}

export function hasSpots(kind = null) {
  try { return collectSlots({ kinds: kind ? [kind] : null }).length > 0 } catch (e) { return false }
}

function fill(words, key, vars) {
  return (words[key] || "").replace(/%\{(\w+)\}/g, (_, v) => (vars[v] == null ? "" : vars[v]))
}

// The words for a slot: "after Workers on site", "before Date", "at the top of
// under Notes"… from the bubble's word list.
export function describeSlot(slot, words) {
  if (slot.kind === "record") {
    if (slot.spot === "form_top") return fill(words, "spot_form_top", {})
    if (slot.spot === "form_bottom") return fill(words, "spot_form_bottom", {})
    const block = fill(words, "spot_landmark_field", { text: slot.landmark.text })
    return fill(words, slot.spot === "before" ? "spot_page_before" : "spot_page_after", { block })
  }
  if (slot.kind === "page") {
    if (slot.spot === "page_top") return fill(words, "spot_page_top", {})
    if (slot.spot === "page_bottom") return fill(words, "spot_page_bottom", {})
    const block = fill(words, `spot_landmark_${slot.landmark.type}`, { text: slot.landmark.text })
    return fill(words, slot.spot === "before" ? "spot_page_before" : "spot_page_after", { block })
  }
  if (slot.kind === "table") return slot.first ? fill(words, "spot_column_first", {}) : fill(words, "spot_column_after", { column: slot.column })
  if (slot.empty) return slot.anchor ? slot.section : fill(words, "spot_top", { section: slot.section })
  if (slot.after_key) return fill(words, "spot_after", { field: slot.prev })
  return fill(words, "spot_before", { field: slot.field })
}

// Only what the server reads: scalars, no page elements.
function payload(slot, words) {
  const out = { kind: slot.kind, label: describeSlot(slot, words) }
  if (slot.kind === "page" || slot.kind === "record") {
    out.spot = slot.spot
    if (slot.landmark) {
      out.landmark_type = slot.landmark.type; out.landmark_text = slot.landmark.text; out.landmark_index = slot.landmark.index
      if (slot.landmark.path) out.landmark_path = slot.landmark.path
    }
    return out
  }
  ;["section_key", "anchor", "after_key", "before_key", "page"].forEach((k) => { if (slot[k]) out[k] = slot[k] })
  if (slot.position != null && !slot.after_key && !slot.before_key) out.position = slot.position
  return out
}

function distance(slot, x, y) {
  if (slot.w > 0) { // a horizontal line
    const dx = x < slot.x ? slot.x - x : x > slot.x + slot.w ? x - slot.x - slot.w : 0
    return Math.hypot(dx, y - slot.y)
  }
  const dy = y < slot.y ? slot.y - y : y > slot.y + slot.h ? y - slot.y - slot.h : 0
  return Math.hypot(x - slot.x, dy)
}

function scrollParent(el) {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    const s = getComputedStyle(n)
    if (/(auto|scroll)/.test(s.overflowY) && n.scrollHeight > n.clientHeight) return n
  }
  return null
}

// Runs the dot over the page; resolves with the spot's data, or null when
// cancelled (Esc, Cancel, or the page changed).
export function pickSpot(words = {}, { kinds = null } = {}) {
  return new Promise((resolve) => {
    let slots = collectSlots({ kinds })
    if (!slots.length) { resolve(null); return }

    const dockPad = parseFloat(getComputedStyle(document.documentElement).paddingRight) || 0
    const shield = document.createElement("div")
    shield.setAttribute("data-studio-spot-picker", "")
    shield.style.cssText = `position:fixed;top:0;left:0;bottom:0;right:${dockPad}px;z-index:9990;cursor:crosshair;background:rgba(255,255,255,.04)`
    const layer = document.createElement("div")
    layer.style.cssText = `position:fixed;top:0;left:0;bottom:0;right:${dockPad}px;z-index:9991;pointer-events:none;overflow:hidden`
    const dot = document.createElement("div")
    dot.style.cssText = `position:fixed;width:22px;height:22px;margin:-11px 0 0 -11px;border-radius:9999px;background:${PURPLE};border:3px solid #fff;box-shadow:0 2px 10px rgba(126,34,206,.45);z-index:9992;pointer-events:none;display:none`
    const tag = document.createElement("div")
    tag.style.cssText = `position:fixed;z-index:9992;pointer-events:none;display:none;background:${PURPLE};color:#fff;font:600 13px/1.2 system-ui,sans-serif;padding:4px 8px;border-radius:8px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.2)`
    const bar = document.createElement("div")
    bar.style.cssText = `position:fixed;top:14px;left:calc(50% - ${dockPad / 2}px);transform:translateX(-50%);z-index:9993;display:flex;align-items:center;gap:10px;background:#1f1333;color:#fff;padding:8px 10px 8px 16px;border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,.3);font:500 14px/1.2 system-ui,sans-serif`
    const hint = document.createElement("span")
    hint.textContent = words.pick_hint || "Click where it goes"
    const done = document.createElement("button")
    done.type = "button"
    done.textContent = words.pick_done || "Done"
    done.disabled = true
    done.style.cssText = `background:${LIGHT};color:#fff;border:0;border-radius:8px;padding:6px 14px;font:600 14px system-ui,sans-serif;cursor:pointer;opacity:.5`
    const cancel = document.createElement("button")
    cancel.type = "button"
    cancel.textContent = words.pick_cancel || "Cancel"
    cancel.style.cssText = "background:transparent;color:#ddd;border:0;padding:6px 8px;font:500 14px system-ui,sans-serif;cursor:pointer"
    bar.append(hint, done, cancel)
    document.body.append(shield, layer, dot, tag, bar)

    let chosen = null
    let near = null
    let dragging = false
    let raf = 0
    const marks = []

    // Anywhere on the page (Phelipe 2026-10-19): dropped away from every drawn line, the dot takes the block
    // under it — before or after that box — remembered by its path and its first words.
    const freeKind = !kinds ? "page" : kinds.includes("record") ? "record" : kinds.includes("page") ? "page" : null
    const freeRoot = () => {
      if (freeKind === "record") { const h = document.querySelector('[data-controller~="custom-fields"]'); return h ? (h.closest("form, [data-custom-fields-root]") || h.parentElement) : null }
      return document.querySelector("main") || document.body
    }
    const free = (x, y) => {
      if (!freeKind) return null
      const root = freeRoot()
      const block = root && blockAt(x, y, root, [shield, layer, dot, tag, bar])
      if (!block) return null
      const r = block.getBoundingClientRect()
      const spot = y < r.top + r.height / 2 ? "before" : "after"
      // A table is one of the page's own landmarks already: name it the usual way ("the "Date" table").
      const known = freeKind === "page" && block.tagName === "TABLE" ? landmarks(root).find((l) => l.el === block) : null
      const landmark = known ? { type: known.type, text: known.label, index: known.index } : { type: "element", text: blockWords(block), path: elementPath(block, root), index: 0 }
      return { kind: freeKind, free: true, x: r.left, y: spot === "before" ? r.top - 6 : r.bottom + 6, w: Math.max(r.width, 40), h: 0, spot, landmark }
    }

    const draw = () => {
      marks.splice(0).forEach((m) => m.remove())
      ;[...slots, ...[chosen, near].filter((s) => s && s.free)].forEach((s) => {
        const m = document.createElement("div")
        const on = s === chosen
        const soft = s === near && !on
        if (s.w > 0) m.style.cssText = `position:fixed;left:${s.x}px;top:${s.y - 1}px;width:${s.w}px;height:3px;border-radius:2px;background:${on ? PURPLE : LIGHT};opacity:${on ? 1 : soft ? .7 : .25}`
        else m.style.cssText = `position:fixed;left:${s.x - 1}px;top:${s.y}px;width:3px;height:${s.h}px;border-radius:2px;background:${on ? PURPLE : LIGHT};opacity:${on ? 1 : soft ? .7 : .25}`
        layer.appendChild(m)
        marks.push(m)
      })
      if (chosen) {
        const cx = chosen.w > 0 ? chosen.x + chosen.w / 2 : chosen.x
        const cy = chosen.w > 0 ? chosen.y : chosen.y + chosen.h / 2
        dot.style.display = "block"
        dot.style.left = `${cx}px`
        dot.style.top = `${cy}px`
        tag.textContent = describeSlot(chosen, words)
        tag.style.display = "block"
        tag.style.left = `${Math.min(cx + 16, window.innerWidth - dockPad - 220)}px`
        tag.style.top = `${cy - 12}px`
      }
    }

    const refresh = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const same = (a, b) => ["kind", "section_key", "after_key", "before_key", "position", "page", "spot"].every((k) => (a[k] ?? null) === (b[k] ?? null))
          && (a.landmark?.type ?? null) === (b.landmark?.type ?? null) && (a.landmark?.text ?? null) === (b.landmark?.text ?? null) && (a.landmark?.index ?? null) === (b.landmark?.index ?? null)
        const keep = chosen
        slots = collectSlots({ kinds })
        chosen = keep ? (keep.free ? keep : slots.find((s) => same(s, keep)) || null) : null
        draw()
      })
    }

    const nearest = (x, y) => {
      let best = null
      let bd = Infinity
      slots.forEach((s) => { const d = distance(s, x, y); if (d < bd) { bd = d; best = s } })
      if (bd > 36) { const f = free(x, y); if (f) return f }
      return best
    }

    const choose = (x, y) => {
      chosen = nearest(x, y)
      done.disabled = !chosen
      done.style.opacity = chosen ? "1" : ".5"
      draw()
    }

    const onMove = (e) => {
      if (!chosen || dragging) { dot.style.display = "block"; dot.style.left = `${e.clientX}px`; dot.style.top = `${e.clientY}px` }
      if (dragging) { choose(e.clientX, e.clientY); return }
      const n = nearest(e.clientX, e.clientY)
      if (n !== near) { near = n; draw() }
    }
    const onDown = (e) => { if (e.button !== 0) return; e.preventDefault(); dragging = true; choose(e.clientX, e.clientY) }
    const onUp = (e) => { if (!dragging) return; dragging = false; choose(e.clientX, e.clientY) }
    const onWheel = (e) => {
      e.preventDefault()
      const under = document.elementsFromPoint(e.clientX, e.clientY).find((n) => n !== shield && n !== layer && !layer.contains(n))
      const sc = under ? scrollParent(under) : null
      if (sc) sc.scrollBy({ top: e.deltaY, left: e.deltaX })
      else window.scrollBy({ top: e.deltaY, left: e.deltaX })
    }
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); finish(null) }
      else if (e.key === "Enter" && chosen) { e.preventDefault(); finish(chosen) }
    }
    const onLeave = () => finish(null)

    const finish = (slot) => {
      shield.removeEventListener("mousemove", onMove)
      shield.removeEventListener("mousedown", onDown)
      window.removeEventListener("mouseup", onUp)
      shield.removeEventListener("wheel", onWheel)
      document.removeEventListener("keydown", onKey, true)
      document.removeEventListener("scroll", refresh, true)
      window.removeEventListener("resize", refresh)
      document.removeEventListener("turbo:before-visit", onLeave)
      ;[shield, layer, dot, tag, bar].forEach((n) => n.remove())
      resolve(slot ? payload(slot, words) : null)
    }

    shield.addEventListener("mousemove", onMove)
    shield.addEventListener("mousedown", onDown)
    window.addEventListener("mouseup", onUp)
    shield.addEventListener("wheel", onWheel, { passive: false })
    document.addEventListener("keydown", onKey, true)
    document.addEventListener("scroll", refresh, true)
    window.addEventListener("resize", refresh)
    document.addEventListener("turbo:before-visit", onLeave)
    done.addEventListener("click", () => chosen && finish(chosen))
    cancel.addEventListener("click", () => finish(null))
    draw()
  })
}
